import { routePaths } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { ChevronLeft } from "lucide-react";
import { Link, useParams } from "react-router";
import ReactSignatureCanvas from "react-signature-canvas";

import { useToast } from "@/components/ui/use-toast";
import { useTakePicture } from "@/hooks/useTakePicture";
import { useTourStop } from "@/libs/tours-detail/hooks/useTourStop";
import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";

/**
 * TODO: This file contains AI generated Code. The code in this file may not be complete.
 * We defenetly need to refactor this.
 */

export function AlternativeDeliveryPage() {
  const { t } = useTranslation();

  const { id, stopId, taskId } = useParams();

  const { toast } = useToast();

  const [recipientName, setRecipientName] = useState("");
  const [recipientAddress, setRecipientAddress] = useState("");
  const [signature, setSignature] = useState<string | null>(null);
  const [note, setNote] = useState("");

  const signatureRef = useRef<ReactSignatureCanvas>(null);

  const { deliverTourStop, uploadDeliveryProofImage, currentStop } =
    useTourStop(stopId as string);

  const { photo, takePicture } = useTakePicture();

  const currentTask = currentStop?.items.find((item) => item.id === taskId);

  const handleSignatureEnd = () => {
    if (signatureRef.current) {
      setSignature(signatureRef.current.toDataURL());
    }
  };

  const handleDeliveryProofUpload = async () => {
    await takePicture();
  };

  const handleRecipientDelivery = () => {
    if (!recipientName || !recipientAddress || !currentTask) {
      toast({
        title: "Fehlende Angaben",
        description:
          "Bitte gib den Namen und die Adresse des Empfängers an, um fortzufahren.",
        variant: "error",
      });
      return;
    }

    if (photo && photo.base64String) {
      uploadDeliveryProofImage(
        photo.base64String,
        currentTask.parentId,
        currentTask.id,
      );
    }

    deliverTourStop(currentTask.id, currentTask.parentId, {
      handedToName: recipientName,
      handedToAddress: recipientAddress,
      driverNote: note,
      signature: signature ?? undefined,
    });
  };

  return (
    <div className="h-dvh bg-slate-100 flex flex-col overflow-y-auto">
      <header className="bg-white px-4 py-6 border-b-2 border-neutral pt-safe">
        <Link
          to={routePaths.createTourStopPath(
            id as string,
            stopId as string,
            "order-delivery",
          )}
          className="mb-8 flex items-center font-medium gap-1.5 bg-slate-100 w-fit p-2 pr-4 rounded"
        >
          <ChevronLeft className="w-5 h-5" strokeWidth={2.5} />
          {t("common.back")}
        </Link>

        <h3 className="text-xl font-semibold">
          {t("alternativeNeighbor.header.title")}
        </h3>
      </header>

      <section className="p-4 space-y-6 overflow-y-scroll flex-1">
        <div className="grid w-full items-center gap-1.5">
          <Label htmlFor="name">{t("alternativeNeighbor.recipient")}</Label>
          <Input
            type="name"
            id="name"
            placeholder={t("alternativeNeighbor.recipient")}
            value={recipientName}
            onChange={(e) => setRecipientName(e.target.value)}
          />
        </div>

        <div className="grid w-full  items-center gap-1.5">
          <Label htmlFor="address">{t("alternativeNeighbor.address")}</Label>
          <Input
            type="address"
            id="address"
            placeholder={t("alternativeNeighbor.address")}
            value={recipientAddress}
            onChange={(e) => setRecipientAddress(e.target.value)}
          />
        </div>

        <div className="grid w-full  items-center gap-1.5">
          <Label htmlFor="note">{t("alternativeNeighbor.note")}</Label>
          <Textarea
            id="note"
            placeholder={t("alternativeNeighbor.note")}
            value={note}
            onChange={(e) => setNote(e.target.value)}
          />
        </div>

        <div className="grid w-full items-center gap-1.5">
          <Label htmlFor="signature">
            {t("alternativeNeighbor.signature")}
          </Label>
          <div className="border rounded-md w-full">
            <ReactSignatureCanvas
              ref={signatureRef}
              onEnd={handleSignatureEnd}
              canvasProps={{
                height: 500,
                className: "bg-white rounded-md w-full",
              }}
            />
          </div>
        </div>

        <div
          className="grid w-full items-center gap-1.5 border-2 border-dashed border-gray-300 h-48 rounded-md cursor-pointer"
          onClick={handleDeliveryProofUpload}
        >
          {photo?.base64String ? (
            <img
              className="w-full h-full object-contain rounded-md"
              src={`data:image/png;base64,${photo.base64String}`}
              alt="Beweisfoto"
            />
          ) : (
            <span className="w-full h-full flex items-center justify-center text-gray-500">
              {t("alternativeNeighbor.uploadImage")}
            </span>
          )}
        </div>
      </section>

      <div className="p-4 bg-white space-y-4 border-t-2 border-neutral pb-safe">
        <Button className="w-full" onClick={handleRecipientDelivery}>
          {t("alternativeNeighbor.submit")}
        </Button>
      </div>
    </div>
  );
}
